import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { ButtonType } from '@xemplo/button';
import { DawnTriangleAlert } from '@xemplo/icons';
import { ModalBodyOverflow, ModalSize, ModalTheme } from '@xemplo/modal';
import { RejectEarningLinesPayload, useRejectEarningLines } from '@xemplo/payrun-query';
import { PromptProps, PromptType } from '@xemplo/prompts';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import * as S from './payrun-approve-reject.styles';
import { RejectEarningLinesType } from './payrun-approve-reject.types';
import {
  RejectAttachment,
  RejectCategory,
  RejectDescription,
} from './reject-prompt-fields';

export const useRejectPrompt = () => {
  const [promptVisible, setPromptVisible] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const methods = useForm<RejectEarningLinesType>();

  const { description, errorCategory } = methods.watch();

  const { id } = useParams();
  const { addToast } = useToast();
  const { mutateAsync: rejectPayrun } = useRejectEarningLines({
    payrunId: id ?? null,
    onSuccess: () => {
      setPromptVisible(false);
      addToast({
        text: 'Pay run rejected',
        type: ToastType.Confirmation,
      });
    },
  });

  const handleSubmit = useCallback(
    async (data: RejectEarningLinesType) => {
      const payload: RejectEarningLinesPayload = {
        description: encodeURI(data.description),
        errorCategory: data.errorCategory,
        errorSource: data.errorSource,
        rejectionReasonID: data.rejectionReasonID,
        attachment: data.attachment?.[0],
      };
      const [, error] = await safeCallAsync(rejectPayrun(payload));
      if (error) return;
      setPromptVisible(false);
    },
    [rejectPayrun],
  );

  const handleReject = useCallback(async () => {
    setShouldValidate(true);

    const isValid = await methods.trigger();
    if (!isValid) return;

    methods.handleSubmit(handleSubmit)();
  }, [methods, handleSubmit]);

  const initialPromptProps = useMemo<PromptProps>(() => {
    return {
      open: false,
      type: PromptType.Alert,
      theme: ModalTheme.Light,
      size: ModalSize.Responsive,
      header: {
        customHeader: (
          <S.PromptHeader>
            <DawnTriangleAlert />
            <div>Reject pay run</div>
          </S.PromptHeader>
        ),
      },
      body: {
        content: (
          <S.PromptBody noValidate>
            <div>
              When rejecting a pay run in payroll, it is important to provide a clear and
              concise explanation of the reason for the rejection. This will help to
              ensure that the issue is resolved as quickly as possible.
            </div>
            <FormProvider {...methods}>
              <RejectCategory shouldValidate={shouldValidate} />
              <RejectDescription shouldValidate={shouldValidate} />
              <RejectAttachment />
            </FormProvider>
          </S.PromptBody>
        ),
        overflow: ModalBodyOverflow.Extended,
      },
      footer: {
        btnPrimary: {
          label: 'Reject',
          disabled: !(description && errorCategory),
          ariaLabel: 'Reject Button',
          onClickAsync: handleReject,
          type: ButtonType.Destructive,
        },
        btnSecondary: {
          label: 'Cancel',
          ariaLabel: 'Cancel Btn',
          type: ButtonType.Secondary,
          onClick: () => setPromptVisible(false),
        },
      },
    };
  }, [methods, shouldValidate, description, errorCategory, handleReject]);

  useEffect(() => {
    if (initialPromptProps.open) {
      setPromptVisible(true);
    }
  }, [initialPromptProps.open]);

  return { promptProps: initialPromptProps, promptVisible, setPromptVisible };
};
